import React, { Suspense, useMemo } from 'react'

import {
  ConfirmationDialog,
  ModalInjectedProps,
  Skeleton,
} from '@sketch/components'
import { useStorageItemsCount } from '../../hooks/useStorageItemsCount'
import { StorageItemIdentity } from '../../types'
import {
  StorageItemFragment,
  StorageItemFragmentDoc,
  useApolloClient,
  useDeleteStorageItemsMutation,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { Link } from 'react-router-dom'
import { deleteWorkspaceProject, routes } from '@sketch/modules-common'
import { handleShareDeleted } from '../../../shares/operations/handlers'
import { useEventDispatch } from '@sketch/utils'
import { useGetStorageItem } from '../../hooks'
import { dataIdFromObject } from '@sketch/graphql-cache'
import { ErrorHandler } from '@sketch/tracing'

interface DeleteStorageItemMessageProps {
  item: StorageItemIdentity
}

const DeleteStorageItemMessage = ({ item }: DeleteStorageItemMessageProps) => {
  const storageItem = useGetStorageItem(item)
  return `“${storageItem?.name}”`
}

interface DeleteStorageItemsMessageProps {
  items: StorageItemIdentity[]
}

const DeleteStorageItemsMessage = ({
  items,
}: DeleteStorageItemsMessageProps) => {
  const { message } = useStorageItemsCount(items)
  return message
}

interface DeleteStorageItemsModalProps extends ModalInjectedProps {
  items: StorageItemIdentity[]
  workspaceId: string
  onDelete?: () => void
}

export const DeleteStorageItemsModal: React.FC<
  DeleteStorageItemsModalProps
> = props => {
  const { items, hideModal, isModalOpen, workspaceId, onDelete } = props

  const { showToast } = useToast()

  const dispatchTrashRefresh = useEventDispatch('workspaceTrashRefresh')
  const dispatchShareRefresh = useEventDispatch('workspaceShareRefresh')
  const client = useApolloClient()

  const projectIdentifiers = useMemo(
    () =>
      items.reduce((set, item) => {
        const storageItem = client.readFragment<StorageItemFragment>({
          fragment: StorageItemFragmentDoc,
          fragmentName: 'StorageItem',
          id: dataIdFromObject(item),
        })

        if (storageItem?.__typename === 'Share') {
          storageItem.project && set.add(storageItem.project.identifier)
        } else if (storageItem?.__typename === 'Project') {
          set.add(storageItem.identifier)
          storageItem.parentProjectIdentifier &&
            set.add(storageItem.parentProjectIdentifier)
        } else {
          ErrorHandler.shouldNeverHappen(
            'We are expected to have the StorageItem fragment at this point, some other cache operation deleted it or a incorrect identifier is being passed'
          )
        }

        return set
      }, new Set<string>()),
    [items, client]
  )

  const [deleteItems, { loading }] = useDeleteStorageItemsMutation({
    variables: {
      storageItems: items.map(item => ({
        identifier: item.identifier,
        type: item.__typename === 'Share' ? 'SHARE' : 'PROJECT',
      })),
    },
    onCompleted: () => {
      dispatchTrashRefresh({ workspaceIdentifier: workspaceId })

      /**
       * We are dispatching the share refresh to all the project and project parents identifier
       * because are not sure from the DeleteStorageItemsModal which view the user currently is
       *
       * By doing this we are sending multiple events, however only one will be answered the
       * on that matches to the view the user is seeing.
       */
      projectIdentifiers.forEach(projectId => {
        dispatchShareRefresh({
          workspaceIdentifier: workspaceId,
          projectIdentifier: projectId,
        })
      })

      hideModal()
      onDelete?.()

      showToast(
        <>
          Selection has been moved to{' '}
          <Link
            to={routes.WORKSPACE_TRASH.create({
              workspaceId,
            })}
          >
            Trash
          </Link>
        </>,
        'positive'
      )
    },
    update: (cache, { data }) => {
      const items = data?.deleteStorageItems.storageItems || []

      items.forEach(item => {
        if (item.__typename === 'Share') {
          handleShareDeleted({
            cache,
            identifier: item.identifier,
            workspaceIdentifier: workspaceId,
          })
        } else {
          deleteWorkspaceProject(cache, workspaceId, item.identifier)
        }

        cache.evict({ id: cache.identify(item) })
      })
    },
    onError: 'show-toast',
  })

  const message =
    items.length === 1 ? (
      <DeleteStorageItemMessage item={items[0]} />
    ) : (
      <DeleteStorageItemsMessage items={items} />
    )

  const title =
    items.length === 1 ? 'Move to Trash?' : 'Move Selection to Trash?'

  return (
    <ConfirmationDialog
      title={title}
      hideModal={hideModal}
      isModalOpen={isModalOpen}
      cancelButton={{
        disabled: loading,
      }}
      confirmButton={{
        text: 'Move to Trash',
        variant: 'negative',
        loading,
        disabled: loading,
      }}
      onConfirm={() => {
        deleteItems()
      }}
    >
      <>
        <p>
          Move{' '}
          <Suspense fallback={<Skeleton width="100px" />}>
            <strong>{message}</strong>
          </Suspense>{' '}
          to Trash?
        </p>
        <p>
          Items in Trash will be permanently deleted after <b>90 days</b>.
          Workspace Members with access can still see it in Trash.
        </p>
      </>
    </ConfirmationDialog>
  )
}
